import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/App";
import { Loader2 } from "lucide-react";

export const STAGE_LABELS = {
  enquiry: "Enquiry", lead: "Lead", consultation: "Consultation", proposal: "Proposal",
  agreement: "Agreement", invoiced: "Invoiced", active_client: "Active client",
  project: "Project", completed: "Completed", retainer: "Monthly retainer",
};
export const STAGE_ORDER = Object.keys(STAGE_LABELS);

const STATUS_TONE = {
  new: "bg-sky-50 text-sky-700 border-sky-200", pending: "bg-amber-50 text-amber-700 border-amber-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200", sent: "bg-indigo-50 text-indigo-700 border-indigo-200",
  pending_signature: "bg-amber-50 text-amber-700 border-amber-200", partially_paid: "bg-amber-50 text-amber-700 border-amber-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200", signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200", confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200", published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200", qualified: "bg-indigo-50 text-indigo-700 border-indigo-200",
  contacted: "bg-violet-50 text-violet-700 border-violet-200", declined: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200", lost: "bg-rose-50 text-rose-700 border-rose-200",
};

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${STATUS_TONE[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
    {STAGE_LABELS[status] || String(status || "—").replace(/_/g, " ")}
  </span>
);

export const StageBadge = ({ stage, testid }) => (
  <span data-testid={testid} className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-900 text-white text-xs font-medium whitespace-nowrap">
    {STAGE_LABELS[stage] || stage}
  </span>
);

export const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export const useCommerceApi = (token) =>
  useCallback(async (path, { method = "GET", body, raw } = {}) => {
    const res = await fetch(`${API}${raw ? "" : "/admin/commerce"}${path}`, {
      method, credentials: "include",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = typeof data.detail === "string" ? data.detail : "Request failed";
      toast.error(msg);
      throw new Error(msg);
    }
    return data;
  }, [token]);

export const useLoad = (call, path) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    return call(path).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [call, path]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, reload };
};

export const Loading = () => (
  <div className="flex items-center justify-center py-16" data-testid="commerce-loading">
    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, testid, children }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center">
    {Icon && <Icon className="h-10 w-10 mx-auto text-slate-300 mb-3" />}
    <p className="font-semibold text-slate-800">{title}</p>
    {text && <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{text}</p>}
    {children && <div className="mt-4">{children}</div>}
  </div>
);

export const Panel = ({ title, action, children, testid, className = "" }) => (
  <div data-testid={testid} className={`bg-white rounded-xl border border-slate-200 ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
        {action}
      </div>
    )}
    {children}
  </div>
);

export const DataTable = ({ columns, rows, rowKey, testid, onRowClick }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
          {columns.map(c => <th key={c.key} className="px-5 py-3 font-medium whitespace-nowrap">{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r[rowKey]} onClick={onRowClick ? () => onRowClick(r) : undefined}
            className={`border-b border-slate-50 last:border-0 ${onRowClick ? "cursor-pointer hover:bg-slate-50" : ""}`}>
            {columns.map(c => <td key={c.key} className="px-5 py-3 align-top">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const copyLink = (url) => {
  navigator.clipboard?.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.info(url));
};

export const origin = () => window.location.origin;
